// components/viewport/InfiniteViewport.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { ViewportEnergyBackground } from '@/components/viewport/ViewportEnergyBackground';
import { ViewportControls } from '@/components/viewport/ViewportControls';
import {
  isRecord,
  isValidBounds,
  normalizeRuntimePath,
  parseKernelViewportState,
  readNestedRuntimeValue,
} from '@/kernel/useViewports';
import type {
  KernelState,
  KernelViewportBounds,
  KernelViewportOwner,
  KernelViewportRuntimeState,
} from '#types/types';

interface InfiniteViewportProps {
  viewportKey: string;
  owner: KernelViewportOwner;
  children: React.ReactNode;
  contentBounds?: KernelViewportBounds | null;
  defaultViewport?: KernelViewportRuntimeState;
  minZoom?: number;
  maxZoom?: number;
  fitMargin?: number;
  dragPanMode?: 'background' | 'anywhere';
  className?: string;
  contentClassName?: string;
  backdropClassName?: string;
}

const DEFAULT_VIEWPORT: KernelViewportRuntimeState = {
  panOffset: { x: 0, y: 0 },
  zoomLevel: 1,
};

const selectViewportState = (
  state: KernelState,
  owner: KernelViewportOwner,
  fallback: KernelViewportRuntimeState
): KernelViewportRuntimeState => {
  const entityId = typeof owner.entityId === 'string' ? owner.entityId.trim() : '';
  const runtimePath = normalizeRuntimePath(owner.runtimePath);
  if (!entityId || runtimePath.length === 0) return fallback;

  const inventory = state.inventory as unknown as Record<string, { registry?: Record<string, { runtime?: Record<string, unknown> }> }>;
  const runtime = inventory[owner.entityType]?.registry?.[entityId]?.runtime;
  if (!isRecord(runtime)) return fallback;
  return parseKernelViewportState(readNestedRuntimeValue(runtime, runtimePath), fallback) ?? fallback;
};

export const InfiniteViewport = React.memo(({
  viewportKey,
  owner,
  children,
  contentBounds = null,
  defaultViewport = DEFAULT_VIEWPORT,
  minZoom,
  maxZoom,
  fitMargin,
  dragPanMode = 'background',
  className,
  contentClassName,
  backdropClassName,
}: InfiniteViewportProps) => {
  const { kernel } = useKernel();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const transformRef = useRef<HTMLDivElement | null>(null);

  const normalizedDefaultViewport = useMemo(
    () => parseKernelViewportState(defaultViewport, DEFAULT_VIEWPORT) ?? DEFAULT_VIEWPORT,
    [defaultViewport.panOffset.x, defaultViewport.panOffset.y, defaultViewport.zoomLevel]
  );
  const runtimePath = useMemo(() => normalizeRuntimePath(owner.runtimePath), [owner.runtimePath]);
  const runtimePathKey = runtimePath.join('\u0000');
  const normalizedBounds = useMemo(() => (isValidBounds(contentBounds) ? contentBounds : null), [contentBounds?.h, contentBounds?.w, contentBounds?.x, contentBounds?.y]);
  const viewport = useKernelStore((state) => selectViewportState(state, owner, normalizedDefaultViewport));

  useEffect(() => {
    if (!kernel.current) return;
    kernel.current.registerKernelViewport({
      key: viewportKey,
      owner: {
        entityType: owner.entityType,
        entityId: owner.entityId,
        runtimePath,
      },
      containerElement: containerRef.current,
      transformElement: transformRef.current,
      contentBounds: normalizedBounds,
      defaultViewport: normalizedDefaultViewport,
      minZoom,
      maxZoom,
      fitMargin,
      dragPanMode,
    });
    return () => {
      kernel.current?.unregisterKernelViewport(viewportKey);
    };
  }, [
    kernel,
    viewportKey,
    owner.entityType,
    owner.entityId,
    runtimePathKey,
    normalizedBounds?.x,
    normalizedBounds?.y,
    normalizedBounds?.w,
    normalizedBounds?.h,
    normalizedDefaultViewport.panOffset.x,
    normalizedDefaultViewport.panOffset.y,
    normalizedDefaultViewport.zoomLevel,
    minZoom,
    maxZoom,
    fitMargin,
    dragPanMode,
  ]);

  useEffect(() => {
    kernel.current?.applyKernelViewportTransform(viewportKey, viewport);
  }, [kernel, viewportKey, viewport.panOffset.x, viewport.panOffset.y, viewport.zoomLevel]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const wheelHandler = (event: WheelEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-viewport-ignore-pan="true"]')) {
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      kernel.current?.handleKernelViewportWheel(viewportKey, event as unknown as React.WheelEvent<HTMLDivElement>);
    };

    container.addEventListener('wheel', wheelHandler, { passive: false });
    return () => {
      container.removeEventListener('wheel', wheelHandler);
    };
  }, [kernel, viewportKey]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    kernel.current?.handleKernelViewportPointerDown(viewportKey, event);
  }, [kernel, viewportKey]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    kernel.current?.handleKernelViewportPointerMove(viewportKey, event);
  }, [kernel, viewportKey]);

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    kernel.current?.handleKernelViewportPointerUp(viewportKey, event);
  }, [kernel, viewportKey]);

  const controlsNode = useMemo(() => (
    <ViewportControls
      viewportKey={viewportKey}
      zoomLevel={viewport.zoomLevel}
      canFit={Boolean(normalizedBounds)}
    />
  ), [normalizedBounds, viewport.zoomLevel, viewportKey]);

  return (
    <div
      ref={containerRef}
      data-infinite-viewport-root="true"
      data-infinite-viewport-background="true"
      className={clsx('relative min-h-[320px] overflow-hidden rounded-xl border border-[#2A2A2A] bg-black', className)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <ViewportEnergyBackground className={backdropClassName} />
      <div
        ref={transformRef}
        data-viewport-transform-container="true"
        className="absolute inset-0 pointer-events-none will-change-transform"
        style={{
          transform: `translate(${viewport.panOffset.x}px, ${viewport.panOffset.y}px) scale(${viewport.zoomLevel})`,
          transformOrigin: '0 0',
          '--zoom-level': Math.max(0.0001, viewport.zoomLevel),
          '--inverse-zoom-level': 1 / Math.max(0.0001, viewport.zoomLevel),
          '--pan-x': `${viewport.panOffset.x}px`,
          '--pan-y': `${viewport.panOffset.y}px`,
        } as React.CSSProperties}
      >
        <div className={clsx('pointer-events-auto absolute left-0 top-0', contentClassName)}>
          {children}
        </div>
      </div>
      <div className="pointer-events-none absolute bottom-3 right-3 z-10">
        {controlsNode}
      </div>
    </div>
  );
});
InfiniteViewport.displayName = 'InfiniteViewport';
